/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

function currency(val) {
  return '$' + parseFloat(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Card({ title, subtitle, children, style }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12, border: '0.5px solid #e0e0db',
      marginBottom: 20, overflow: 'hidden', ...style,
    }}>
      <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #f0f0f0' }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

function Badge({ bg, color, label }) {
  return (
    <span style={{
      background: bg, color, fontSize: 10, padding: '2px 7px',
      borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap', marginLeft: 6,
    }}>{label}</span>
  );
}

// ============================================================
// Section 1: Driver Rates
// ============================================================
function DriverRatesSection({ drivers, onDriverUpdate }) {
  const [editingId, setEditingId] = useState(null);
  const [editRate, setEditRate] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (driver) => {
    setEditingId(driver.id);
    setEditRate(driver.hourly_rate || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditRate('');
  };

  const saveRate = async (driverId) => {
    setSaving(true);
    try {
      await api.put(`/drivers/${driverId}`, { hourlyRate: parseFloat(editRate) });
      onDriverUpdate();
      setEditingId(null);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Driver Rates" subtitle="Hourly pay rates and schedule types for all active drivers">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e0e0db' }}>
              <th style={thStyle}>Driver</th>
              <th style={thStyle}>Hourly Rate</th>
              <th style={thStyle}>Schedule</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => (
              <tr key={d.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500 }}>{d.first_name} {d.last_name}</span>
                  {d.is_full_time && <Badge bg="#E1F5EE" color="#0F6E56" label="FT" />}
                </td>
                <td style={tdStyle}>
                  {editingId === d.id ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editRate}
                      onChange={e => setEditRate(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRate(d.id); if (e.key === 'Escape') cancelEdit(); }}
                      style={{ width: 80, fontSize: 13, padding: '3px 6px', border: '1px solid #185FA5', borderRadius: 4 }}
                      autoFocus
                    />
                  ) : (
                    <span style={{ fontFamily: 'monospace' }}>{currency(d.hourly_rate)}</span>
                  )}
                </td>
                <td style={tdStyle}>
                  <span style={{ color: '#666' }}>
                    {d.schedule_type === 'set' ? 'Set schedule' : d.schedule_type === 'flexible' ? 'Flexible' : d.schedule_type || 'Not set'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <Badge
                    bg={d.status === 'active' ? '#E1F5EE' : '#FFF3E0'}
                    color={d.status === 'active' ? '#0F6E56' : '#BA7517'}
                    label={d.status || 'active'}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {editingId === d.id ? (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button onClick={() => saveRate(d.id)} disabled={saving} style={btnSmall}>
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={cancelEdit} style={{ ...btnSmall, background: '#f0f0f0', color: '#666', border: '0.5px solid #e0e0db' }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(d)} style={{ ...btnSmall, background: '#f0f0f0', color: '#444', border: '0.5px solid #e0e0db' }}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ============================================================
// Section 2: Facility Billing Rules
// ============================================================
function FacilityRulesSection({ facilityRules, billingRates }) {
  return (
    <Card title="Facility Billing Rules" subtitle="How each facility is billed — currently set in code">
      <div style={{ display: 'grid', gap: 12 }}>
        {facilityRules.map((f, i) => (
          <div key={i} style={{
            background: '#fafaf8', borderRadius: 8, padding: '12px 16px',
            border: '0.5px solid #f0f0f0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{f.name}</span>
              <Badge
                bg={f.billingType === 'patient' ? '#FFF3E0' : '#E6F1FB'}
                color={f.billingType === 'patient' ? '#BA7517' : '#185FA5'}
                label={f.billingType === 'patient' ? 'Bill to patient' : 'Bill to facility'}
              />
            </div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>
              {f.notes}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#444' }}>
              <span>Sedan: {currency(f.sedanMileageRate)}/mi</span>
              <span>WC: {currency(f.wcMileageRate)}/mi</span>
              <span>Transporters: {f.minTransporters}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#f5f5f3', borderRadius: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Global Billing Rates</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <div><span style={{ color: '#999' }}>Hourly rate:</span> <strong>{currency(billingRates.hourlyRate)}/hr</strong></div>
          <div><span style={{ color: '#999' }}>Sedan mileage:</span> <strong>{currency(billingRates.sedanMileageRate)}/mi</strong></div>
          <div><span style={{ color: '#999' }}>WC mileage:</span> <strong>{currency(billingRates.wheelchairMileageRate)}/mi</strong></div>
          <div><span style={{ color: '#999' }}>Cleanup fee:</span> <strong>{currency(billingRates.cleanupFee)}</strong></div>
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// Section 3: Toll Table
// ============================================================
function TollTableSection({ tolls, onTollsUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [editTolls, setEditTolls] = useState({});
  const [newDest, setNewDest] = useState('');
  const [newAmt, setNewAmt] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setEditTolls({ ...tolls });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditTolls({});
    setNewDest('');
    setNewAmt('');
  };

  const updateAmount = (key, val) => {
    setEditTolls(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));
  };

  const removeEntry = (key) => {
    setEditTolls(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const addEntry = () => {
    if (!newDest.trim() || !newAmt) return;
    setEditTolls(prev => ({ ...prev, [newDest.toLowerCase().trim()]: parseFloat(newAmt) || 0 }));
    setNewDest('');
    setNewAmt('');
  };

  const saveTolls = async () => {
    setSaving(true);
    try {
      await api.put('/settings/tolls', { tolls: editTolls });
      onTollsUpdate();
      setEditMode(false);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const displayTolls = editMode ? editTolls : tolls;
  const sortedKeys = Object.keys(displayTolls).sort();

  return (
    <Card title="Toll Table" subtitle="Auto-applied tolls based on destination keywords">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {!editMode ? (
          <button onClick={startEdit} style={{ ...btnSmall, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5' }}>
            Edit tolls
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={saveTolls} disabled={saving} style={{ ...btnSmall, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={cancelEdit} style={{ ...btnSmall, background: '#f0f0f0', color: '#666', border: '0.5px solid #e0e0db' }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {sortedKeys.map(key => (
          <div key={key} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#fafaf8', borderRadius: 6, padding: '8px 12px',
            border: '0.5px solid #f0f0f0',
          }}>
            <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{key}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {editMode ? (
                <>
                  <input
                    type="number"
                    step="0.01"
                    value={editTolls[key]}
                    onChange={e => updateAmount(key, e.target.value)}
                    style={{ width: 70, fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 4, textAlign: 'right' }}
                  />
                  <button onClick={() => removeEntry(key)} style={{ background: 'none', border: 'none', color: '#E24B4A', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>
                    x
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 500 }}>{currency(displayTolls[key])}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {editMode && (
        <div style={{
          marginTop: 12, display: 'flex', gap: 8, alignItems: 'center',
          padding: '10px 12px', background: '#f5f5f3', borderRadius: 8,
        }}>
          <input
            value={newDest}
            onChange={e => setNewDest(e.target.value)}
            placeholder="Destination keyword"
            style={{ flex: 1, fontSize: 13, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <input
            type="number"
            step="0.01"
            value={newAmt}
            onChange={e => setNewAmt(e.target.value)}
            placeholder="Amount"
            style={{ width: 80, fontSize: 13, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <button onClick={addEntry} style={{ ...btnSmall, background: '#1D9E75', color: 'white', border: 'none' }}>
            Add
          </button>
        </div>
      )}
    </Card>
  );
}

// ============================================================
// Section 4: Overnight Rules
// ============================================================
function OvernightRulesSection({ rules }) {
  const shifts = rules.shifts || [];
  return (
    <Card title="Overnight On-Call Rules" subtitle="11pm - 7am staffing rules">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
        <InfoTile label="Overnight hours" value={`${rules.overnightStart} - ${rules.overnightEnd}`} />
        <InfoTile label="Billing" value={rules.alwaysBillTwoTransporters ? 'Always 2 transporters' : 'Standard'} accent="#0F6E56" />
      </div>
      {shifts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Driver</th>
              <th style={{ padding: '6px 8px' }}>Role</th>
              <th style={{ padding: '6px 8px' }}>Days</th>
              <th style={{ padding: '6px 8px' }}>Window</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '6px 8px', fontWeight: s.role === 'primary' ? 600 : 400 }}>{s.driverName}</td>
                <td style={{ padding: '6px 8px', color: s.role === 'primary' ? '#185FA5' : '#BA7517' }}>{s.role}</td>
                <td style={{ padding: '6px 8px' }}>{s.days}</td>
                <td style={{ padding: '6px 8px' }}>{s.window}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 12, fontSize: 11, color: '#999' }}>
        Managed via Settings &gt; Overnight Shifts in the admin API. Changes take effect within 5 minutes.
      </div>
    </Card>
  );
}

function InfoTile({ label, value, accent }) {
  return (
    <div style={{
      background: '#fafaf8', borderRadius: 8, padding: '10px 14px',
      border: '0.5px solid #f0f0f0',
    }}>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: accent || '#333' }}>{value}</div>
    </div>
  );
}

// ============================================================
// Section 5: Vehicle Nicknames
// ============================================================
function VehicleNicknamesSection({ nicknames }) {
  // Group by resolved name to show aliases together
  const grouped = {};
  for (const [alias, resolved] of Object.entries(nicknames)) {
    if (!grouped[resolved]) grouped[resolved] = [];
    grouped[resolved].push(alias);
  }

  return (
    <Card title="Vehicle Nicknames" subtitle="Shorthand names Rosie understands when dispatching">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {Object.entries(grouped).map(([vehicle, aliases]) => (
          <div key={vehicle} style={{
            background: '#fafaf8', borderRadius: 8, padding: '10px 14px',
            border: '0.5px solid #f0f0f0',
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{vehicle}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {aliases.map(a => (
                <span key={a} style={{
                  background: '#E6F1FB', color: '#185FA5', fontSize: 11,
                  padding: '2px 8px', borderRadius: 12,
                }}>
                  {a}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: '#999' }}>
        These nicknames are set in code (adminChat.js). To add new ones, ask Rosie.
      </div>
    </Card>
  );
}

// ============================================================
// Section 6: Payroll Rates
// ============================================================
function PayrollRatesSection({ payrollRates }) {
  return (
    <Card title="Payroll Rates" subtitle="Global payroll calculation rules">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        <InfoTile label="Default hourly rate" value={currency(payrollRates.defaultHourlyRate) + '/hr'} />
        <InfoTile label="Senior hourly rate" value={currency(payrollRates.seniorHourlyRate) + '/hr'} />
        <InfoTile label="Minimum hours" value={payrollRates.minimumHours + ' hr'} />
        <InfoTile label="Rounding" value={'Up to nearest ' + (payrollRates.roundingIncrement * 60) + ' min'} />
        <InfoTile label="Cleanup bonus" value={currency(payrollRates.cleanupBonus)} />
        <InfoTile label="Meal reimbursement" value={`${currency(payrollRates.mealReimbursementMax)} (>${payrollRates.mealReimbursementMileMin} mi)`} />
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: '#999' }}>
        Set in dispatchEngine.js. These apply globally unless a driver has a custom hourly rate.
      </div>
    </Card>
  );
}

// ============================================================
// Section 7: Holidays
// ============================================================
function HolidaysSection({ holidays, onHolidaysUpdate }) {
  const [editing, setEditing] = useState(false);
  const [editHolidays, setEditHolidays] = useState({});
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [newMult, setNewMult] = useState('1.5');
  const [showPast, setShowPast] = useState(false);

  const sortedDates = Object.keys(editing ? editHolidays : holidays).sort();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const futureHolidays = sortedDates.filter(d => d >= today);
  const pastHolidays = sortedDates.filter(d => d < today);
  const displayHolidays = editing ? editHolidays : holidays;

  const startEdit = () => { setEditHolidays({ ...holidays }); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setNewDate(''); setNewName(''); };

  const saveHolidays = async () => {
    setSaving(true);
    try {
      await api.put('/settings/holidays', { holidays: editHolidays });
      setEditing(false);
      if (onHolidaysUpdate) onHolidaysUpdate();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const removeHoliday = (date) => {
    const updated = { ...editHolidays };
    delete updated[date];
    setEditHolidays(updated);
  };

  const addHoliday = () => {
    if (!newDate || !newName) return;
    setEditHolidays({ ...editHolidays, [newDate]: { name: newName, multiplier: parseFloat(newMult) || 1.5 } });
    setNewDate(''); setNewName(''); setNewMult('1.5');
  };

  const updateMultiplier = (date, mult) => {
    setEditHolidays({ ...editHolidays, [date]: { ...editHolidays[date], multiplier: parseFloat(mult) || 1.5 } });
  };

  return (
    <Card title="Holiday Multipliers" subtitle="Pay and billing multipliers for holidays">
      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        {!editing ? (
          <button onClick={startEdit} style={{ ...btnSmall, background: '#E6F1FB', color: '#185FA5' }}>Edit holidays</button>
        ) : (
          <>
            <button onClick={saveHolidays} disabled={saving} style={{ ...btnSmall, background: '#185FA5', color: 'white' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={cancelEdit} style={{ ...btnSmall, background: '#f0f0f0', color: '#666' }}>Cancel</button>
          </>
        )}
      </div>

      {editing && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
            style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
          <input placeholder="Holiday name" value={newName} onChange={e => setNewName(e.target.value)}
            style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 180 }} />
          <select value={newMult} onChange={e => setNewMult(e.target.value)}
            style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }}>
            <option value="1.5">1.5x</option>
            <option value="2.0">2.0x</option>
          </select>
          <button onClick={addHoliday} disabled={!newDate || !newName}
            style={{ ...btnSmall, background: newDate && newName ? '#185FA5' : '#ccc', color: 'white' }}>+ Add</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
        {futureHolidays.map(date => {
          const h = displayHolidays[date];
          return (
            <div key={date} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#fafaf8', borderRadius: 6, padding: '8px 12px',
              border: '0.5px solid #f0f0f0',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{h.name}</div>
                <div style={{ fontSize: 11, color: '#999' }}>{date}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {editing ? (
                  <>
                    <select value={h.multiplier} onChange={e => updateMultiplier(date, e.target.value)}
                      style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4 }}>
                      <option value="1.5">1.5x</option>
                      <option value="2.0">2.0x</option>
                    </select>
                    <button onClick={() => removeHoliday(date)}
                      style={{ fontSize: 11, background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>Remove</button>
                  </>
                ) : (
                  <Badge
                    bg={h.multiplier >= 2.0 ? '#FCEBEB' : '#FFF3E0'}
                    color={h.multiplier >= 2.0 ? '#A32D2D' : '#BA7517'}
                    label={h.multiplier + 'x'}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pastHolidays.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setShowPast(!showPast)}
            style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {showPast ? 'Hide' : 'Show'} {pastHolidays.length} past holiday(s)
          </button>
          {showPast && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6, marginTop: 8, opacity: 0.6 }}>
              {pastHolidays.map(date => {
                const h = displayHolidays[date];
                return (
                  <div key={date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafaf8', borderRadius: 6, padding: '6px 10px', border: '0.5px solid #f0f0f0' }}>
                    <div>
                      <div style={{ fontSize: 12 }}>{h.name}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{date}</div>
                    </div>
                    <Badge bg={h.multiplier >= 2.0 ? '#FCEBEB' : '#FFF3E0'} color={h.multiplier >= 2.0 ? '#A32D2D' : '#BA7517'} label={h.multiplier + 'x'} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ============================================================
// Shared styles
// ============================================================
const thStyle = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#999',
  fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em',
};

const tdStyle = {
  padding: '10px 10px', fontSize: 13, verticalAlign: 'middle',
};

const btnSmall = {
  fontSize: 12, padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
  border: 'none', fontWeight: 500,
};

// ============================================================
// Section 8: LGH iPad Room (PB-2) — device tokens + tap-your-name roster
// ============================================================
function LghIpadSection() {
  const [tokens, setTokens] = useState([]);
  const [names, setNames] = useState('');
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(null); // { token, url } shown once
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([
        api.get('/settings/device-tokens'),
        api.get('/settings/lgh-roster'),
      ]);
      setTokens(t.data.tokens || []);
      setNames((r.data.names || []).join('\n'));
    } catch (e) { setMsg('Could not load iPad settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    if (!label.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const res = await api.post('/settings/device-tokens', { label: label.trim(), roomKey: 'lgh' });
      setIssued({ token: res.data.token, url: res.data.url });
      setLabel('');
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not issue token'); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this iPad link? The device will lose access immediately.')) return;
    setBusy(true); setMsg(null);
    try { await api.post(`/settings/device-tokens/${id}/revoke`); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not revoke'); } finally { setBusy(false); }
  };

  const saveRoster = async () => {
    setBusy(true); setMsg(null);
    const list = names.split('\n').map((n) => n.trim()).filter(Boolean);
    try { await api.put('/settings/lgh-roster', { names: list }); setMsg('Roster saved.'); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save roster'); } finally { setBusy(false); }
  };

  return (
    <Card title="LGH iPad Room" subtitle="Device links for the shared LGH iPad and the tap-your-name list">
      {msg && <div style={{ background: '#FFF7E6', color: '#8A5A00', padding: '6px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{msg}</div>}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Issue a new iPad link</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder='Label (e.g. "LGH ED iPad")'
            style={{ flex: 1, fontSize: 14, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8 }} />
          <button onClick={issue} disabled={busy || !label.trim()}
            style={{ background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, cursor: 'pointer', opacity: busy || !label.trim() ? 0.5 : 1 }}>Issue</button>
        </div>
        {issued && (
          <div style={{ background: '#E7F6F0', border: '1px solid #b7e3d2', borderRadius: 8, padding: 12, marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#085041', marginBottom: 4 }}>Open this once on the iPad, then it stays signed in. You won’t see it again.</div>
            <div style={{ fontSize: 13, wordBreak: 'break-all', fontFamily: 'monospace', color: '#0F6E56' }}>{issued.url}</div>
            <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(issued.url)}
              style={{ marginTop: 8, background: 'white', border: '1px solid #b7e3d2', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#0F6E56' }}>Copy link</button>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Active links</div>
        {tokens.length === 0 && <div style={{ fontSize: 13, color: '#999' }}>No iPad links issued yet.</div>}
        {tokens.map((t) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div>
              <div style={{ fontSize: 14 }}>{t.label} {!t.is_active && <span style={{ color: '#A32D2D', fontSize: 12 }}>· revoked</span>}</div>
              <div style={{ fontSize: 11, color: '#999' }}>
                room {t.room_key} · last used {t.last_used_at ? new Date(t.last_used_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never'}
              </div>
            </div>
            {t.is_active && (
              <button onClick={() => revoke(t.id)} disabled={busy}
                style={{ background: 'white', color: '#A32D2D', border: '1px solid #E3B3B3', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Revoke</button>
            )}
          </div>
        ))}
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Tap-your-name list (one name per line)</div>
        <textarea value={names} onChange={(e) => setNames(e.target.value)} rows={6}
          placeholder={'Sarah (ED)\nMike (charge)\n…'}
          style={{ width: '100%', fontSize: 14, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box', fontFamily: 'inherit' }} />
        <button onClick={saveRoster} disabled={busy}
          style={{ marginTop: 8, background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>Save names</button>
      </div>
    </Card>
  );
}

// ============================================================
// Main Page
// ============================================================
export default function BusinessRules() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/business-rules');
      setData(res.data);
    } catch (err) {
      setError('Failed to load business rules: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  if (loading) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center', color: '#999', fontSize: 14 }}>
        Loading business rules...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center' }}>
        <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '12px 20px', borderRadius: 8, display: 'inline-block', fontSize: 13 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ paddingTop: 24, paddingBottom: 40 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Business Rules</h1>
        <p style={{ fontSize: 13, color: '#999', margin: 0 }}>
          All rates, rules, and settings that drive billing, payroll, and dispatch
        </p>
      </div>

      <DriverRatesSection drivers={data.drivers} onDriverUpdate={fetchRules} />
      <FacilityRulesSection facilityRules={data.facilityRules} billingRates={data.billingRates} />
      <TollTableSection tolls={data.tolls} onTollsUpdate={fetchRules} />
      <PayrollRatesSection payrollRates={data.payrollRates} />
      <OvernightRulesSection rules={data.overnightRules} />
      <VehicleNicknamesSection nicknames={data.vehicleNicknames} />
      <HolidaysSection holidays={data.holidays} onHolidaysUpdate={fetchRules} />
      <LghIpadSection />
    </div>
  );
}
